import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2, FileSpreadsheet, FileText } from "lucide-react";

// V1.59 · Exportar alumnos — selector de columnas, filtro y formato (Excel/PDF)

const FILTERS = [
  ["todos", "Todos los alumnos"],
  ["aptos", "Aptos"],
  ["no_aptos", "No aptos"],
  ["factura_emitida", "Con factura emitida"],
  ["a_examen", "A examen (citas futuras)"],
];

const DEFAULT_COLS = ["nombre", "primer_apellido", "segundo_apellido", "dni", "telefono", "email", "estado"];

export default function ExportStudentsDialog({ open, onClose }) {
  const [columns, setColumns] = useState([]);
  const [selected, setSelected] = useState(DEFAULT_COLS);
  const [filter, setFilter] = useState("todos");
  const [format, setFormat] = useState("xlsx");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/students/export/columns")
      .then(({ data }) => setColumns(data || []))
      .catch(() => toast.error("No se pudieron cargar las columnas"));
  }, [open]);

  const toggle = (k) =>
    setSelected((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  const doExport = async () => {
    if (selected.length === 0) { toast.error("Selecciona al menos una columna"); return; }
    setBusy(true);
    try {
      const res = await api.post("/students/export", { columns: selected, format, filter }, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const fname = (cd.match(/filename="([^"]+)"/) || [])[1] || `alumnos.${format}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportación descargada");
    } catch {
      toast.error("No se pudo exportar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="export-students-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-orange-500" /> Exportar alumnos
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold mb-1 block">Alumnos a exportar</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="export-filter"
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
              {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold mb-1 block">Columnas ({selected.length})</label>
            <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto rounded-lg border border-neutral-200 p-2">
              {columns.map((c) => (
                <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={selected.includes(c.key)} onChange={() => toggle(c.key)}
                    data-testid={`export-col-${c.key}`} className="h-4 w-4 accent-orange-500" />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold mb-1 block">Formato</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setFormat("xlsx")} data-testid="export-format-xlsx"
                className={"flex-1 h-10 rounded-lg border text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors " + (format === "xlsx" ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-neutral-300 bg-white text-neutral-600")}>
                <FileSpreadsheet className="h-4 w-4" /> Excel (.xlsx)
              </button>
              <button type="button" onClick={() => setFormat("pdf")} data-testid="export-format-pdf"
                className={"flex-1 h-10 rounded-lg border text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors " + (format === "pdf" ? "border-red-400 bg-red-50 text-red-800" : "border-neutral-300 bg-white text-neutral-600")}>
                <FileText className="h-4 w-4" /> PDF
              </button>
            </div>
          </div>

          <button type="button" onClick={doExport} disabled={busy} data-testid="export-download-btn"
            className="btn-primary w-full !h-11">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Generando…</> : <><Download className="h-4 w-4" /> Descargar</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
